import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";

export function ManutenzioneFormDialog({
  open, onClose, editingId, form, setForm, onSubmit, saving,
  automezzi, tipiIntervento, addRiga, updateRiga, removeRiga,
  formatCurrency, getImportoTotale, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingId ? t("maintenance.editMaintenance") : t("maintenance.newMaintenance")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('leasing.vehicle')} *</Label>
              <SearchableSelect
                options={automezzi.map(a => ({ value: a.id, label: `${a.marca} ${a.modello} (${a.targa})` }))}
                value={form.automezzo_id} onValueChange={(v) => setForm({ ...form, automezzo_id: v })}
                placeholder={t("dialogs.selectVehicle")} searchPlaceholder={t("dialogs.searchVehicle")} testId="select-veicolo"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('common.date')} *</Label>
              <DateInput value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("dialogs.interventions")}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addRiga}><Plus className="h-4 w-4 mr-1" /> {t("dialogs.addRow")}</Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
              {form.righe.map((riga, index) => (
                <div key={`riga-${index}`} className="space-y-2 p-2 border rounded bg-white">
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <SearchableSelect
                        options={[{ value: "_none", label: `-- ${t("dialogs.none")} --` }, ...tipiIntervento.map(tp => ({ value: tp.id, label: tp.nome }))]}
                        value={riga.tipo_intervento_id || "_none"}
                        onValueChange={(v) => updateRiga(index, 'tipo_intervento_id', v === "_none" ? "" : v)}
                        placeholder={t("dialogs.selectInterventionType")} searchPlaceholder={t("dialogs.searchInterventionType")} testId={`select-tipo-intervento-${index}`}
                      />
                    </div>
                    <div className="flex-[2]">
                      <Input placeholder={t("dialogs.interventionDescription")} value={riga.descrizione} onChange={(e) => updateRiga(index, 'descrizione', e.target.value)} className="h-9" />
                    </div>
                    <div className="w-[120px]">
                      <Input type="number" step="0.01" placeholder={t("common.amount")} value={riga.importo} onChange={(e) => updateRiga(index, 'importo', e.target.value)} className="h-9" />
                    </div>
                    <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-red-500" onClick={() => removeRiga(index)} disabled={form.righe.length <= 1}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2 pl-1">
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">{t("dialogs.nextInterventionDate")}</Label>
                      <DateInput value={riga.data_prossima || ''} onChange={(e) => updateRiga(index, 'data_prossima', e.target.value)} className="h-8 text-xs" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">{t("dialogs.nextInterventionKm")}</Label>
                      <Input type="number" value={riga.km_prossimo_intervento || ''} onChange={(e) => updateRiga(index, 'km_prossimo_intervento', e.target.value)} placeholder="Es: 160000" className="h-8 text-xs" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-2 border-t mt-2">
                <div className="text-sm">
                  <span className="text-muted-foreground mr-2">{t("common.total")}:</span>
                  <span className="font-bold text-lg">{formatCurrency(getImportoTotale())}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("dialogs.currentKm")}</Label>
              <Input type="number" value={form.km_attuali} onChange={(e) => setForm({ ...form, km_attuali: e.target.value })} placeholder="Es: 150000" />
            </div>
            <div className="space-y-2">
              <Label>{t("dialogs.workshop")}</Label>
              <Input value={form.officina} onChange={(e) => setForm({ ...form, officina: e.target.value })} placeholder={t("dialogs.workshopName")} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("common.notes")}</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("common.notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.save"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TipoInterventoDialog({
  open, onClose, tipiIntervento, tipoForm, setTipoForm,
  editing, setEditing, onSave, onDelete, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("maintenance.manageTypes")}</DialogTitle>
          <DialogDescription>{t("dialogs.manageTypesDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {tipiIntervento.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t("dialogs.noInterventionType")}</p>
            ) : (
              tipiIntervento.map(tipo => (
                <div key={tipo.id} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <span className="font-medium">{tipo.nome}</span>
                    {tipo.is_system && <Badge variant="secondary" className="ml-2 text-xs">{t("dialogs.system")}</Badge>}
                    {tipo.descrizione && <span className="text-xs text-muted-foreground ml-2">- {tipo.descrizione}</span>}
                  </div>
                  {!tipo.is_system && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(tipo); setTipoForm({ nome: tipo.nome, descrizione: tipo.descrizione || "" }); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(tipo)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">{editing ? t("dialogs.editType") : t("dialogs.newInterventionType")}</h4>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">{t("common.name")} *</Label>
                <Input value={tipoForm.nome} onChange={(e) => setTipoForm({ ...tipoForm, nome: e.target.value })} placeholder={t("dialogs.exampleOilChange")} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("common.description")}</Label>
                <Input value={tipoForm.descrizione} onChange={(e) => setTipoForm({ ...tipoForm, descrizione: e.target.value })} placeholder={t("dialogs.descriptionOptional")} />
              </div>
              <div className="flex gap-2">
                {editing && (
                  <Button variant="outline" onClick={() => { setEditing(null); setTipoForm({ nome: "", descrizione: "" }); }}>{t("dialogs.cancelEdit")}</Button>
                )}
                <Button onClick={onSave}>{editing ? t("common.save") : t("dialogs.addType")}</Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ManutenzioneDeleteDialog({ open, onClose, onDelete, t }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
          <DialogDescription>{t("dialogs.deleteMaintenance")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="destructive" onClick={onDelete}>{t("common.delete")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LinkContabilitaDialog({
  open, onClose, linkDialog, fatture, movimenti,
  onLinkFattura, onLinkMovimento, formatCurrency, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {linkDialog.type === 'fattura' ? t("maintenance.linkInvoice") : t("maintenance.linkBankMovement")}
          </DialogTitle>
          <DialogDescription>
            {linkDialog.item && `Manutenzione ${linkDialog.item.officina || ''} - ${linkDialog.item.data}`}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto">
          {linkDialog.type === 'fattura' && (
            <div className="space-y-2">
              {linkDialog.item?.fattura_id && (
                <>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                    <p className="text-sm font-medium text-green-800 mb-2">{t("dialogs.linkInvoice")}:</p>
                    {(() => {
                      const f = fatture.find(fa => fa.id === linkDialog.item.fattura_id);
                      return f ? (
                        <div className="text-sm text-green-700 space-y-1">
                          <p><span className="font-medium">{t("invoices.invoiceNumber")}:</span> {f.numero}</p>
                          <p><span className="font-medium">{t("common.date")}:</span> {f.data}</p>
                          <p><span className="font-medium">{t("common.supplier")}:</span> {f.fornitore || 'N/D'}</p>
                          <p><span className="font-medium">{t("common.type")}:</span> {f.tipo || 'N/D'}</p>
                          <p className="font-medium">{t("common.amount")}: {formatCurrency(f.totale)}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-green-700">{t("dialogs.linkInvoice")} N. {linkDialog.item.fattura_numero}</p>
                      );
                    })()}
                  </div>
                  <Button variant="outline" className="w-full justify-start text-red-600" onClick={() => onLinkFattura("_none")}>
                    {t("dialogs.removeLink")}
                  </Button>
                </>
              )}
              {!linkDialog.item?.fattura_id && (
                <SearchableSelect
                  options={fatture.map(f => ({ value: f.id, label: `${f.numero} - ${f.fornitore_cliente} (${formatCurrency(f.totale)})` }))}
                  onValueChange={onLinkFattura}
                  placeholder={t("dialogs.selectInvoice")} searchPlaceholder={t("dialogs.searchInterventionType")} testId="select-link-fattura"
                />
              )}
            </div>
          )}
          {linkDialog.type === 'movimento' && (
            <div className="space-y-2">
              {linkDialog.item?.movimento_bancario_id && (
                <>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                    <p className="text-sm font-medium text-blue-800 mb-2">{t("dialogs.linkBankMovement")}:</p>
                    {(() => {
                      const m = movimenti.find(mo => mo.id === linkDialog.item.movimento_bancario_id);
                      return m ? (
                        <div className="text-sm text-blue-700 space-y-1">
                          <p><span className="font-medium">{t("common.description")}:</span> {m.descrizione || 'N/D'}</p>
                          <p><span className="font-medium">{t("dialogs.accountingDate")}:</span> {m.data}{m.data_valuta ? ` | ${t("dialogs.valueDate")}: ${m.data_valuta}` : ''}</p>
                          <p><span className="font-medium">{t("dialogs.bank")}:</span> {m.banca_nome || 'N/D'}</p>
                          <p><span className="font-medium">{t("common.type")}:</span> {m.tipo === 'entrata' ? t("dialogs.income") : t("dialogs.expense")}</p>
                          <p className={`font-medium ${m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                            {t("common.amount")}: &euro; {formatCurrency(Math.abs(m.importo))}
                          </p>
                          {m.note && <p><span className="font-medium">{t("common.notes")}:</span> {m.note}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-blue-700">{t("dialogs.linkBankMovementNoDetails")}</p>
                      );
                    })()}
                  </div>
                  <Button variant="outline" className="w-full justify-start text-red-600" onClick={() => onLinkMovimento(null)}>
                    {t("dialogs.removeLink")}
                  </Button>
                </>
              )}
              {!linkDialog.item?.movimento_bancario_id && (
                movimenti.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t("maintenance.noMovements")}</p>
                ) : (
                  movimenti.map(m => (
                    <Button key={m.id} variant="outline" className="w-full justify-between" onClick={() => onLinkMovimento(m.id)}>
                      <span className="truncate max-w-xs">{m.descrizione || 'N/D'}</span>
                      <span className={m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}>
                        {m.data} &bull; {formatCurrency(Math.abs(m.importo))}
                      </span>
                    </Button>
                  ))
                )
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CollegaFatturaDialog({
  open, onClose, allocazione, manutenzioni, automezzi,
  selectedManutenzioni, toggleManutenzione, onCollega, formatCurrency, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("maintenance.linkToInvoice")}</DialogTitle>
          <DialogDescription>
            Fattura: {allocazione?.fattura_numero} - {allocazione?.fornitore} ({formatCurrency(allocazione?.importo)})
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-4">
          {manutenzioni.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">{t("maintenance.noMaintenance")}</p>
          ) : (
            manutenzioni.filter(m => !m.fattura_id).map(man => {
              const auto = automezzi.find(a => a.id === man.automezzo_id);
              return (
                <div
                  key={man.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedManutenzioni.includes(man.id) ? 'bg-green-50 border-green-300' : 'hover:bg-gray-50'}`}
                  onClick={() => toggleManutenzione(man.id)}
                >
                  <Checkbox checked={selectedManutenzioni.includes(man.id)} onCheckedChange={() => toggleManutenzione(man.id)} />
                  <div className="flex-1">
                    <div className="font-medium">{auto ? `${auto.marca} ${auto.modello} (${auto.targa})` : t("dialogs.vehicleNotFound")}</div>
                    <div className="text-sm text-muted-foreground">{man.data} - {man.descrizione_righe || man.descrizione || '-'} - {formatCurrency(man.importo_totale || man.importo)}</div>
                    {man.officina && <div className="text-xs text-muted-foreground">{t("dialogs.workshop")}: {man.officina}</div>}
                  </div>
                </div>
              );
            })
          )}
          {manutenzioni.length > 0 && manutenzioni.every(m => m.fattura_id) && (
            <p className="text-center text-muted-foreground py-4">{t("dialogs.linkInvoiceMaintenance")}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={onCollega} disabled={selectedManutenzioni.length === 0} className="bg-green-600 hover:bg-green-700">
            {t("dialogs.linkToMaintenance")} {selectedManutenzioni.length} {t("dialogs.maintenanceItems")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
